import objectPath from 'object-path';
import { fromNowInUtc, daysSince, formatUtcDateTimeToLocal } from 'src/utils/date';
import { UAParser } from 'ua-parser-js';

import linkedInLogo from 'src/logos/linkedin.svg';
import twitterLogo from 'src/logos/twitter.svg';
import facebookLogo from 'src/logos/facebook.svg';

const displayedSocialMediaDomains = ['linkedin', 'twitter', 'facebook'];

export type DeviceFormType = 'mobile' | 'tablet' | 'desktop';
export type OsType = 'windows' | 'macos' | 'ios' | 'android';
export type DeviceType = {
  form?: DeviceFormType;
  os?: OsType;
};

const SocialMediaDomainTypes = ['facebook', 'twitter', 'linkedin'] as const;
export type SocialMediaDomainType = typeof SocialMediaDomainTypes[number];

export type SocialMedia = {
  url: string;
  type: SocialMediaDomainType;
  handle: string;
};

const getSocialMediaUrl = (person: any, domain: string) => {
  if (!person || !person[domain]) return ''; 

  let baseUrl;
  switch (domain) {
    case 'linkedin':
    case 'twitter':
    case 'facebook':
      baseUrl = `${domain}.com`;
      break;

    default:
       return '';
  }

  // some linkedin profiles don't start with in/ prefix, so adding conditionally
  if (domain === 'linkedin' && !person[domain].handle.startsWith('in/')) {
    person[domain].handle = `in/${person[domain].handle}`;
  }

  return `https://${baseUrl}/${person[domain].handle}`;
};

const getSocialMediaLogoUrl = (domain: string) => {
  if (domain === 'linkedin') return linkedInLogo;
  if (domain === 'twitter') return twitterLogo;
  if (domain === 'facebook') return facebookLogo;

  return null;
};

export const buildInsightsViewDetails = (view: any) => {
  const clearbit_data = view.visitor.clearbit_data;
  const clearbit_reveal_data = view.visitor.clearbit_reveal_data;
  const visitor_custom_fields = view.visitor.visitor_custom_fields;
  const visitor_fields = view.visitor;

  const personName = (clearbit_data: any, visitor_custom_fields:any) => {
    if(visitor_custom_fields && visitor_custom_fields.full_name){
        return visitor_custom_fields.full_name
    }

    if(visitor_fields && visitor_fields.visitor_name){
      return visitor_fields.visitor_name
    }

    if (!clearbit_data) return null;

    const { person, full_name } = clearbit_data;

    if ( (!person || !person.name) && !full_name) return null;

    let returnName = full_name;

    if(returnName){
      return returnName
        .split(' ')
        .map((word:string) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
        .join(' ');
    }

    return objectPath.get(person, 'name.fullName', null);
    
  };

  const occupation = (clearbit_data: any) => {    
    if (!clearbit_data) return null;

    const { person, job_title, job_company_name } = clearbit_data;

    let employmentTitle = null;
    let employmentName = null;

    if (person){
      employmentTitle = objectPath.get(person, 'employment.title', null);
      employmentName = objectPath.get(person, 'employment.name', null);
    }
    else{
      employmentTitle = job_title
      employmentName = job_company_name
    }
   

    if (!employmentTitle || !employmentName || employmentTitle === 'null' || employmentName === 'null') {
      return null;
    }

    return `${employmentTitle} @ ${employmentName}`;
  };

  const occupationTitle = (clearbit_data: any) => {
    if (!clearbit_data) return null;

    const { person, job_title } = clearbit_data;

    let employmentTitle = null;

    if (person){
      employmentTitle = objectPath.get(person, 'employment.title', null);
    }
    else{
      employmentTitle = job_title
    }

    if (!employmentTitle || employmentTitle === 'null') {
      return null;
    }

    return employmentTitle;
  };

  const companyDetails = (clearbit_person_data: any, clearbit_reveal_data: any, visitor_custom_fields: any) => {

    let companyName = null, companyDomain=null;

    if(visitor_custom_fields && visitor_custom_fields.company_name){
        companyName = visitor_custom_fields.company_name
    }

    if (!clearbit_person_data && !clearbit_reveal_data) {
      if(companyName){
        return {
          name: companyName
        }
      }  
      return null;
    }

    let { company, job_company_name, job_company_website } = clearbit_person_data || clearbit_reveal_data || {};
    const companyLogo = objectPath.get(company, 'logo', null);
    
    if(companyName==null){
      companyName = objectPath.get(company, 'name', null);
    }
    if(!companyName){
      companyName = job_company_name || null
    }

    if (!companyName) {
      return null;
    }

    companyDomain = objectPath.get(company, 'domain', null);

    if(!companyDomain){
      companyDomain = objectPath.get(company, 'website', null);
    }

    if(!companyDomain){
      companyDomain = job_company_website || null
    }

    return {
      logo: companyLogo,
      name: companyName,
      domain: companyDomain,
    };
  };

  const personalInfo = (personName: any, occupation: any) => {
    if (!personName || personName === 'null') return null;

    if (occupation && occupation !== 'null') {
      return `${personName}, ${occupation}`;
    }

    return personName;
  };


  const revealSomeoneFrom = (clearbit_reveal_data: any) => {
    if (!clearbit_reveal_data) return null;
    const { company, domain } = clearbit_reveal_data || {};

    let companyName = null, companyDomain = domain;

    if(companyName==null){
      companyName = objectPath.get(company, 'name', null);
    }

    if (!companyName) return null;

    companyDomain = domain;

    if(!companyDomain){
      companyDomain = objectPath.get(company, 'website', null);
    }
    

    return `Someone from ${companyName || companyDomain}`;
  };

  const generateSocialMediaLinks = (clearbit_person_data: any, clearbit_reveal_data: any) => {
    if (!clearbit_person_data && !clearbit_reveal_data) return [];

    const { person, profiles, linkedin_url,  linkedin_username, facebook_url, facebook_username, twitter_url, twitter_username } = clearbit_person_data || {};
    const { company } = clearbit_reveal_data || {};

    if (!person && !profiles  && !company) return [];

    const socialIdentifier = person || company;

    let isSocialContainerVisible = linkedin_url || facebook_url || twitter_url

    if(isSocialContainerVisible){
      return ([
        ...(linkedin_url?[{
          domain: 'linkedin',
          handle: linkedin_username,
          url: linkedin_url,
          logoUrl: getSocialMediaLogoUrl('linkedin'),
        }]:[]),
        ...(facebook_url?[{
          domain: 'facebook',
          handle: facebook_username,
          url: facebook_url,
          logoUrl: getSocialMediaLogoUrl('facebook'),
        }]:[]),
        ...(twitter_url?[{
          domain: 'twitter',
          handle: twitter_username,
          url: twitter_url,
          logoUrl: getSocialMediaLogoUrl('twitter'),
        }]:[])
      ])
    }

    isSocialContainerVisible = displayedSocialMediaDomains.some(
      (social) => socialIdentifier && socialIdentifier[social] && socialIdentifier[social].handle
    );

    if (!isSocialContainerVisible) return [];

    return displayedSocialMediaDomains.reduce((arr: any, domain: string) => {
      if (socialIdentifier && socialIdentifier[domain] && socialIdentifier[domain].handle) {
        arr.push({
          domain: domain,
          handle: socialIdentifier[domain].handle,
          url: getSocialMediaUrl(socialIdentifier, domain),
          logoUrl: getSocialMediaLogoUrl(domain),
        });
      }

      return arr;
    }, []);
  };

  const utmParams = (view_params: any, utm_parameters: any) => {
    let utmParams: any = {};

    // First check for utm_parameters object from the backend
    if (utm_parameters && typeof utm_parameters === 'object') {
      Object.entries(utm_parameters).forEach(([key, value]) => {
        if (key.startsWith('utm_')) {
          utmParams[key] = value;
        }
      });
    }

    // Then check view_params (the old way) as fallback
    if (Object.keys(utmParams).length === 0 && view_params) {
      Object.entries(view_params || {}).forEach(([key, value]) => {
        if (key.startsWith('utm_')) {
          utmParams[key] = value;
        }
      });
    }

    return Object.entries(utmParams)
      .map(([key, value]) => `${key}=${value}`)
      .join('&');
  };

  const getLocation = (view: any) => {
    // ip address is the actual location string
    // however it might include strings like 'None' etc, so turn them into 'Unknown';
    const { location } = view;
    if (!location) return 'Unknown';
    if (location.startsWith('None') || location.endsWith('None')) return 'Unknown';

    return location;
  };

  const getIdentifier = (email: any) => {
    if (email) return email;
  };

  const getDomain = (clearbit_data: any) => {
    if (!clearbit_data) return null;

    let domain = clearbit_data?.company?.domain;

    if(!domain){
      domain = clearbit_data?.company?.website
    }

    return domain
  };

  const getDeviceType = (view: any): DeviceType | undefined => {
    const user_agent = view?.user_agent;

    if (!user_agent) {
      return undefined;
    }

    let device_form_type: DeviceFormType | undefined;
    let os_type: OsType | undefined;

    const parsed_result = new UAParser(user_agent).getResult();
    // This is (almost) what 'react-device-detect' library uses so we use the same
    const parsedDeviceType = (parsed_result?.device.type || '').toLowerCase();
    const parsedOsName = parsed_result?.os.name || '';
    if (parsedDeviceType === 'mobile') {
      device_form_type = 'mobile';
    } else if (parsedDeviceType === 'tablet') {
      device_form_type = 'tablet';
    } else {
      // assume desktop if not tablet or mobile
      device_form_type = 'desktop';
    }

    if (parsedOsName === 'Android') {
      os_type = 'android';
    }
    if (parsedOsName === 'iOS') {
      os_type = 'ios';
    }
    if (parsedOsName === 'Windows') {
      os_type = 'windows';
    }
    if (parsedOsName === 'Mac OS') {
      os_type = 'macos';
    }

    if (!device_form_type && !os_type) {
      return undefined;
    }
    return {
      form: device_form_type,
      os: os_type,
    };
  };

  const getSocialMedia = (clearbit_person_data: any, clearbit_reveal_data: any): SocialMedia[] => {
    if (!clearbit_person_data && !clearbit_reveal_data) return [];

    const { person, profiles, linkedin_url,  linkedin_username, facebook_url, facebook_username, twitter_url, twitter_username } = clearbit_person_data || {};
    const { company } = clearbit_reveal_data || {};

    if (!person && !profiles && !company) return [];

    const socialIdentifier = person || company;

    let isSocialContainerVisible = linkedin_url || facebook_url || twitter_url

    if(isSocialContainerVisible){
      return ([
        ...(linkedin_url?[{
          type: 'linkedin' as SocialMediaDomainType,
          handle: linkedin_username,
          url: linkedin_url,
        }]:[]),
        ...(facebook_url?[{
          type: 'facebook' as SocialMediaDomainType,
          handle: facebook_username,
          url: facebook_url,
        }]:[]),
        ...(twitter_url?[{
          type: 'twitter' as SocialMediaDomainType,
          handle: twitter_username,
          url: twitter_url,
        }]:[])
      ])
    }

    isSocialContainerVisible = SocialMediaDomainTypes.some(
      (social) => socialIdentifier && socialIdentifier[social] && socialIdentifier[social].handle
    );

    if (!isSocialContainerVisible) return [];

    return SocialMediaDomainTypes.filter((social) => socialIdentifier && socialIdentifier[social] && socialIdentifier[social].handle).map(
      (social) => ({
        handle: socialIdentifier[social].handle,
        type: social,
        url: getSocialMediaUrl(socialIdentifier, social),
      })
    );
  };

  const getSessionReplayUrl = (session_replay_uuid: any) => {
    if(session_replay_uuid){
      return `https://app.journey.io/sessions/${session_replay_uuid}`
    }
    return null
  }

  const getUtmParameters = (view: any) => {
    // Return the raw utm_parameters object for direct access
    return view.utm_parameters || {};
  }

  return {
    ...view,
    company: companyDetails(clearbit_data, clearbit_reveal_data,visitor_custom_fields),
    expandable: true,
    personal_info:
      personalInfo(personName(clearbit_data,visitor_custom_fields), occupation(clearbit_data)) ||
      revealSomeoneFrom(clearbit_reveal_data) ||
      (!view.email ? 'Anonymous' : null),
    utm_params: utmParams(view.view_params, view.utm_parameters),
    utm_parameters: getUtmParameters(view),
    email: getIdentifier(view.email),
    website: getDomain(clearbit_data),
    completed_percentage:
      view.completed_percentage && view.completed_percentage > 100 ? 100 : view.completed_percentage,
    social_media_links: generateSocialMediaLinks(clearbit_data, clearbit_reveal_data),
    ip_address: view.ip_address,
    location: getLocation(view),

    // New ones we added for bedrock
    name: personName(clearbit_data,visitor_custom_fields) || view.email,
    occupation_title: occupationTitle(clearbit_data),
    device_type: getDeviceType(view),
    social_media: getSocialMedia(clearbit_data, clearbit_reveal_data),
    session_replay_url: getSessionReplayUrl(view.session_replay_uuid),
    created_at:
      daysSince(view.created_at) > 1 ? formatUtcDateTimeToLocal(view.created_at) : fromNowInUtc(view.created_at),
  };
};
